import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import { MaskedLinesOnView, Reveal } from '../components/Reveal';
import { FAQ_GROUPS } from '../data/site';

export default function FAQ() {
  return (
    <>
      <SEO
        title="FAQ | Thula Doula Wellness — Humble, TX"
        description="Answers about booking through Jane, first visits, prenatal massage safety, policies, and more at Thula Doula Wellness in Humble, TX."
      />
      <PageHeader
        testId="faq-page-header"
        eyebrow="FAQ"
        lines={['Everything you\u2019re', 'wondering, answered.']}
        sub="Booking, visits, prenatal care, and policies — the honest answers, in plain language."
      />

      <section className="bg-brand-cream pb-24 md:pb-32" data-testid="faq-section">
        <div className="mx-auto max-w-5xl px-5 md:px-10">
          {FAQ_GROUPS.map((group, gi) => (
            <div key={group.title} className={gi > 0 ? 'mt-16' : ''} data-testid={`faq-group-${gi}`}>
              <MaskedLinesOnView
                as="h2"
                lines={[group.title + '.']}
                className="font-display text-3xl font-light tracking-tight text-brand-ink md:text-4xl"
              />
              <Reveal delay={0.1} className="mt-8">
                <Accordion type="single" collapsible className="w-full">
                  {group.faqs.map((f, i) => (
                    <AccordionItem key={i} value={`g${gi}-q${i}`} className="border-brand-ink/10">
                      <AccordionTrigger
                        data-testid={`faq-trigger-${gi}-${i}`}
                        className="py-6 text-left text-base font-semibold text-brand-ink hover:text-brand-teal hover:no-underline md:text-lg"
                      >
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="pb-6 text-sm leading-relaxed text-brand-moss md:text-base">
                        {f.a}
                        {f.link && (
                          <Link
                            to={f.link.to}
                            data-testid={`faq-policy-link-${gi}-${i}`}
                            className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] text-brand-teal transition-colors duration-300 hover:text-brand-ink"
                          >
                            {f.link.label}
                            <ArrowUpRight className="size-3.5" />
                          </Link>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Reveal>
            </div>
          ))}

          <Reveal delay={0.1}>
            <div className="mt-20 rounded-3xl bg-brand-parchment p-8 text-center md:p-14" data-testid="faq-cta">
              <h3 className="font-display text-3xl font-light italic tracking-tight text-brand-ink md:text-4xl">
                Still curious?
              </h3>
              <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-brand-moss md:text-base">
                Call us at (832) 350-8516 or send a message — real people answer here.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-4">
                <Link
                  to="/contact"
                  data-testid="faq-contact-link"
                  className="group inline-flex items-center gap-2 rounded-full border border-brand-ink/25 px-7 py-3.5 text-sm font-semibold text-brand-ink transition-[background-color,color,border-color] duration-300 hover:border-brand-ink hover:bg-brand-ink hover:text-brand-cream"
                >
                  Contact us
                </Link>
                <a
                  data-testid="faq-book-btn"
                  href="https://thuladoulamassage.janeapp.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-2.5 rounded-full bg-brand-teal px-7 py-3.5 text-sm font-semibold text-brand-cream transition-[background-color,transform] duration-300 hover:bg-brand-ink active:scale-[0.97]"
                >
                  Book a Massage
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
